import { Suspense, lazy, useState } from 'react';
import {
    Button, Menu, MenuItem, MenuList, Divider, ListSubheader,
    ListItemIcon, ListItemText,
    alpha
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import DifferenceIcon from '@mui/icons-material/Difference';
import HistoryIcon from '@mui/icons-material/History';
import VerifiedIcon from '@mui/icons-material/Verified';
import TuneIcon from '@mui/icons-material/Tune';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { Localized } from '@fluent/react';
import { DocumentReportPrinter, PrinterMode } from './DocumentReportPrinter';
import { DocumentNewVersionDialog } from './DocumentNewVersionDialog';
import { CompanyFeatureFlags, useHasFeatureFlag } from '../../settings/featureFlags';
import { DocumentSysUpdateDialog } from './DocumentSysUpdateDialog';
import { DocumentOwnerDialog } from './DocumentOwnerDialog';
import { useAppDispatch } from '../../../app/hooks';
import { downloadRiskAnalysisReport } from '../riskAnalysis/riskAnalysisApi';
import { showError } from '../../notifications/notificationsSlice';

// Loaded only once Compare is opened, so neither the compare view nor node-htmldiff reaches
// the main bundle for the sessions that never use it.
const CompareSnapshotDialog = lazy(() => import('../compare/CompareSnapshotDialog')
    .then(m => ({ default: m.CompareSnapshotDialog })));

export const DocumentToolbarMenu = ({ documentId, documentType = '' }) => {
    const dispatch = useAppDispatch();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [mode, setMode] = useState<PrinterMode>('download');
    const [newVersionDialogOpen, setNewVersionDialogOpen] = useState(false);
    const [documentSysUpdateDialog, setDocumentSysUpdateDialog] = useState(false);
    const [documentOwnerDialogOpen, setDocumentOwnerDialogOpen] = useState(false);
    const [compareDialogOpen, setCompareDialogOpen] = useState(false);
    const hasDocumentSysUpdate = useHasFeatureFlag(CompanyFeatureFlags.DocumentSysUpdate);
    const [generatingDocx, setGeneratingDocx] = useState(false);

    const open = Boolean(anchorEl);
    const handleClick = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleGenerateDocxClick = async () => {
        handleClose();
        setGeneratingDocx(true);
        try {
            const result = await dispatch(downloadRiskAnalysisReport(String(documentId)));
            if (downloadRiskAnalysisReport.fulfilled.match(result)) {
                const link = document.createElement('a');
                link.href = result.payload.blobUrl;
                link.setAttribute('download', result.payload.fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
            } else {
                dispatch(showError('error'));
            }
        } finally {
            setGeneratingDocx(false);
        }
    };

    const handleMenuItemClick = (selectedMode: PrinterMode) => {
        setMode(selectedMode);
        setIsDialogOpen(true);
        handleClose();
    };
    const handleNewVersionClick = () => {
        setNewVersionDialogOpen(true);
        handleClose();
    };

    const handleDocumentSysUpdateClick = () => {
        setDocumentSysUpdateDialog(true);
        handleClose();
    };

    const handleDocumentOwnerClick = () => {
        setDocumentOwnerDialogOpen(true);
        handleClose();
    };

    const handleCompareClick = () => {
        setCompareDialogOpen(true);
        handleClose();
    };

    return (
        <>
            <Button
                aria-controls={open ? 'document-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                variant="contained"
                color="primary"
                size="small"
                endIcon={<ArrowDropDownIcon />}
            >
                <Localized id="document"><span>_Document</span></Localized>
            </Button>

            <Menu
                id="document-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: { sx: { minWidth: 280, py: 0 } }
                }}
            >
                <MenuList
                    dense
                    subheader={
                        <ListSubheader
                            disableSticky
                            sx={(t) => ({
                                typography: 'overline',
                                letterSpacing: 0.5,
                                bgcolor: alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.20 : 0.12),
                                color: t.palette.primary.main,
                                fontSize: 12,
                                lineHeight: 1.6,
                                py: 0.75,
                            })}
                        >
                            Wydruki
                        </ListSubheader>
                    }
                    sx={{ p: 0 }}
                >
                    <MenuItem onClick={() => handleMenuItemClick('open')} >
                        <ListItemIcon><PrintIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={<Localized id="report-open"><span>_Open report</span></Localized>} />
                    </MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick('download')}>
                        <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={<Localized id="report-download"><span>_Download report</span></Localized>} />
                    </MenuItem>
                    {documentType === 'RA' &&
                        <MenuItem onClick={handleGenerateDocxClick} disabled={generatingDocx}>
                            <ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary={<Localized id="report-generate-docx"><span>Generate docx</span></Localized>} />
                        </MenuItem>
                    }

                </MenuList>

                <Divider sx={{ my: 0.5 }} />

                <MenuList
                    dense
                    subheader={
                        <ListSubheader
                            disableSticky
                            sx={(t) => ({
                                typography: 'overline',
                                letterSpacing: 0.5,
                                bgcolor: alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.20 : 0.12),
                                color: t.palette.primary.main,
                                fontSize: 12,
                                lineHeight: 1.6,
                                py: 0.75,
                            })}
                        >
                            Wersjonowanie
                        </ListSubheader>
                    }
                    sx={{ p: 0 }}
                >
                    <MenuItem onClick={handleNewVersionClick}>
                        <ListItemIcon><DifferenceIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText primary={<Localized id="document-create-new-version"><span>_Nowa wersja</span></Localized>} />
                    </MenuItem>
                    <MenuItem onClick={handleCompareClick}>
                        <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={<Localized id="document-compare-action"><span>_Porównaj</span></Localized>} />
                    </MenuItem>
                </MenuList >

                <Divider sx={{ my: 0.5 }} />

                <MenuList
                    dense
                    subheader={
                        <ListSubheader
                            disableSticky
                            sx={(t) => ({
                                typography: 'overline',
                                letterSpacing: 0.5,
                                bgcolor: alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.20 : 0.12),
                                color: t.palette.primary.main,
                                fontSize: 12,
                                lineHeight: 1.6,
                                py: 0.75,
                            })}
                        >
                            Akcje
                        </ListSubheader>
                    }
                    sx={{ p: 0 }}
                >
                    <MenuItem>
                        <ListItemIcon><VerifiedIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={<Localized id="document-validate"><span>_Waliduj dokument</span></Localized>} />
                    </MenuItem>
                    <MenuItem onClick={handleDocumentOwnerClick}>
                        <ListItemIcon><ManageAccountsIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={<Localized id="document-action-change-owner"><span>_Zmień właściciela dokumentu</span></Localized>} />
                    </MenuItem>
                    {hasDocumentSysUpdate && (<MenuItem onClick={handleDocumentSysUpdateClick}>
                        <ListItemIcon><TuneIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={<Localized id="document-sys-update-action"><span>_Wymuś zmiany w dokumencie</span></Localized>} />
                    </MenuItem>)}
                </MenuList>
            </Menu >

            <DocumentReportPrinter
                documentId={documentId}
                language=""
                mode={mode}
                isDialogOpen={isDialogOpen}
                closeDialog={() => setIsDialogOpen(false)}
            />

            {
                newVersionDialogOpen && (
                    <DocumentNewVersionDialog
                        documentId={documentId}
                        onSuccess={() => setNewVersionDialogOpen(false)}
                        onCancel={() => setNewVersionDialogOpen(false)}
                    />
                )
            }

            {
                documentSysUpdateDialog && (
                    <DocumentSysUpdateDialog
                        documentId={documentId}
                        onSuccess={() => setDocumentSysUpdateDialog(false)}
                        onCancel={() => setDocumentSysUpdateDialog(false)}
                    />
                )
            }

            {
                compareDialogOpen && (
                    <Suspense fallback={null}>
                        <CompareSnapshotDialog
                            documentId={documentId}
                            onCancel={() => setCompareDialogOpen(false)}
                        />
                    </Suspense>
                )
            }

            {
                documentOwnerDialogOpen && (
                    <DocumentOwnerDialog
                        documentId={documentId}
                        onSuccess={() => setDocumentOwnerDialogOpen(false)}
                        onCancel={() => setDocumentOwnerDialogOpen(false)}
                    />
                )
            }
        </>
    );
};
